import { inject, Injectable } from '@angular/core';
import { RouterStateSnapshot, ActivatedRouteSnapshot, Router, ResolveFn, CanActivateFn } from '@angular/router';
import { Observable } from 'rxjs';
import { OrganizationService } from 'services/organization/organization.service';
import { first, map, take } from 'rxjs/operators';
import {
  IPermissions,
  IPolicies,
  PolicyPermissionsService,
} from 'services/policy-permission/policy-permissions.service';

export const organizationResolver: ResolveFn<Observable<any>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(OrganizationService).getOrganizationInformation().pipe(first());
};


export const organizationGroupResolver: ResolveFn<Observable<any>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(OrganizationService).getOrganizationGroups().pipe(map(resp => resp));
};

export const organizationSystemModuleResolver: ResolveFn<Observable<any>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  return inject(OrganizationService).getOrganizationSystemModules().pipe(map(resp => resp));
};

export type LOCATION_MODULE_POLICIES_KEYS = 'locationsFeatureFlag' | 'locationSettings';

const LOCATION_MODULE_POLICIES: IPolicies<LOCATION_MODULE_POLICIES_KEYS> = {
  locationsFeatureFlag: {
    name: 'locationsFeatureFlag',
    url: 'global/feature_flag/locations',
    action: 'enable',
  },
  locationSettings: {
    name: 'locationSettings',
    url: 'settings/organizations/organization_locations',
    action: 'view',
  },
};

@Injectable()
export class LocationModuleGuardService  {
  constructor(private router: Router, private policyPermissionsService: PolicyPermissionsService) {}

  canActivate(route: ActivatedRouteSnapshot) {
    return this.policyPermissionsService.hasMultiplePermissions(Object.values(LOCATION_MODULE_POLICIES)).pipe(
      take(1),
      map((permissions: IPermissions) => {
        const { locationsFeatureFlag, locationSettings } = permissions;
        if (locationsFeatureFlag && locationSettings) {
          return true;
        }
        this.router.navigate(['/access-restricted']);
        return false;
      }),
    );
  }
}

export const locationModuleGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    return inject(LocationModuleGuardService).canActivate(route);
};

@Injectable()
export class NotificationModuleGuardService {
  constructor(private router: Router, private policyPermissionsService: PolicyPermissionsService) {}

  canActivate(route: ActivatedRouteSnapshot) {
    return this.policyPermissionsService.hasPermissions(
      'settings/organization/organization_notifications',
      'view',
    ).pipe(
      take(1),
      map(({ view }) => {
        if (view) {
          return true;
        }
        this.router.navigate(['/access-restricted']);
        return false;
      }),
    );
  }
}

export const notificationModuleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(NotificationModuleGuardService).canActivate(route);
};
